import { InterlacingSection } from '@insify/components';
import { Modal } from '@insify/design-system/components';
import { ShieldCheck } from '@insify/design-system/line-icons';
import { headingXs, headingXxxs, textMd, textXs } from '@insify/design-system/typography';
import {
  AddOns,
  ContactOptions,
  CoverageInputSelect,
  Layout,
  Receipt,
  SectionHeader,
  Shop,
  ShopPNC,
  hasher,
  useProspect,
  useWizard,
} from '@insify/funnel-components';
import { RequestErrorClientSideError } from '@insify/funnel-components/src/utils/commandErrors';
import { formatMoney } from '@insify/global-money';
import { useBreakpoint } from '@insify/react-hooks';
import { TrackedAnchor, TrackedDiv } from '@objectiv/tracker-react';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { getRealEstateAgentHintIfApplicable } from '../../components/realEstateAgentHint';
import { useAddOns } from '../../hooks/useAddOns';
import { useCommands } from '../../hooks/useCommands';
import { useCoverageContent } from '../../hooks/useCoverageContent';
import { useCoverageValues } from '../../hooks/useCoverageValues';
import { useConditionsQuery, usePricingQuery } from '../../hooks/useQueries';
import { GTMEvent, useTagManager } from '../../hooks/useTagManager';

import styles from './Coverages.module.css';
import { BelowSubmit } from './components/BelowSubmit';
import { DeductibleInput } from './components/DeductibleInput';
import { slugToGhostCard } from './components/GhostCard';
import { CarInsurance } from './components/GhostCard/CarInsurance';
import { LiabilityCardOverlay } from './components/LiabilityCardOverlay';
import { USP } from './components/USP';
import { useCoverageOptions } from './userCoverageOptions';

import type { CoverageKey, PropertyMultiRiskKey } from '../../models/coverage';
import type { GhostCardSlug } from '../../models/queries';
import type { MoneyField } from '@insify/global-money';

const SectionBackground = (props: React.ComponentProps<(typeof InterlacingSection)['Background']>) => {
  const isDesktop = useBreakpoint('(min-width: 960px)');
  return <InterlacingSection.Background {...props} startPosition='bottom' startColor={isDesktop ? 'light' : 'dark'} />;
};

const Section = ({ children, ...props }: React.ComponentProps<typeof InterlacingSection>) => (
  <SectionBackground {...props}>
    <InterlacingSection.Content>{children}</InterlacingSection.Content>
  </SectionBackground>
);

/** We only want to trigger the daisycon event once. Even if the user navigates away and back and sees a different price. */
let daisyconEventTriggered = false;

export const CoveragePage = () => {
  const prospectId = useProspect();
  const { setIsLoading, ...coverageValues } = useCoverageValues();
  const { showPreviousPage, showNextPage } = useWizard();
  const { professionalLiabilityOptions, publicLiabilityOptions } = useCoverageOptions();
  const { sendCommand } = useCommands();
  const { t } = useTranslation(['funnel', 'common']);
  const { data: conditionsData } = useConditionsQuery();
  const addOns = useAddOns();

  const ghostCards = conditionsData?.ghostCards ?? [];
  const ghostCardsWithoutCarInsurance = ghostCards.filter((gc) => gc !== 'carInsurance') as Exclude<
    GhostCardSlug,
    'carInsurance'
  >[];
  const isDesktop = useBreakpoint('(min-width: 960px)');
  const [hasModalOpen, setHasModalOpen] = useState(false);
  const { sendEvent } = useTagManager();
  const { error } = usePricingQuery();

  const riskCarriers = conditionsData?.riskCarriers;
  if (riskCarriers && riskCarriers.length !== 1) {
    Sentry.captureException('There should be exactly one risk carrier. Check the Airtable configuration', {
      extra: {
        riskCarriers,
      },
    });
  }

  const riskCarrier = riskCarriers?.[0];

  const onSelect = ({ type, coverLimit }: { type: CoverageKey; coverLimit: MoneyField }) => {
    setIsLoading(true);
    switch (type) {
      case 'professionalLiability': {
        void sendCommand({
          type: 'selectProfessionalLiabilityCoverLimit',
          payload: { coverLimit },
        });
      }
    }
  };

  const onToggle = ({ type, enabled }: { type: CoverageKey | 'carInsurance'; enabled: boolean }) => {
    setIsLoading(true);
    switch (type) {
      case 'publicLiability': {
        const payload = enabled ? publicLiabilityOptions.enabled : publicLiabilityOptions.disabled;
        void sendCommand({
          type: 'selectPublicLiabilityCoverLimit',
          payload,
        });
        break;
      }
      case 'carInsurance': {
        const payload = { interested: enabled };
        void sendCommand({
          type: 'flagCarInsuranceInterest',
          payload,
        });
      }
    }
  };

  const onSelectDeductible = ({ amount }: { amount: MoneyField }) => {
    setIsLoading(true);
    void sendCommand({
      type: 'selectDeductible',
      payload: { deductible: amount },
    });
  };

  const onPropertyChange = ({ type, amount }: { type: PropertyMultiRiskKey; amount: MoneyField }) => {
    setIsLoading(true);
    console.log(type, amount);
    /* void sendCommand({
      type,
      payload: { sumInsured: amount },
    }); */
  };

  const ContactUs: React.FC<{ children: React.ReactNode }> = ({ children }): JSX.Element => {
    return (
      <p className={clsx(textXs, styles.smallPrint)}>
        <TrackedAnchor
          objectiv={{ id: 'contact-us' }}
          href='https://www.insify.fr/contact/'
          target='_blank'
          rel='noreferrer'
        >
          {t('funnel:coverage_liability_contact_us_link')}
        </TrackedAnchor>{' '}
        {children}
      </p>
    );
  };

  const coverageContent = useCoverageContent();

  const sendGTMEvent = async (): Promise<void> => {
    const premiumInCents = Number(coverageValues.premiums.total?.amount?.cents || 0);
    const pncConversionValue = String((premiumInCents / 100) * 12);
    sendEvent(GTMEvent.DAISYCON_PNC_LEAD, {
      pncConversionValue,
      transactionId: await hasher('SHA-256', prospectId),
      compensationCode: 'rcpro_lead',
    });
  };

  useEffect(() => {
    if (!daisyconEventTriggered && coverageValues.premiums.total?.amount?.cents) {
      void sendGTMEvent();
      daisyconEventTriggered = true;
    }
  }, [coverageValues.premiums.total?.amount?.cents]);

  return (
    <div>
      {conditionsData?.professionalLiability?.dailyCancellable ?? true ? (
        <Shop.TopUSP>
          <Shop.TopUSP.Check />
          <Shop.TopUSP.TextWithStrong>
            <Trans i18nKey='funnel:coverage_top_usp' />
          </Shop.TopUSP.TextWithStrong>
        </Shop.TopUSP>
      ) : null}
      <Layout.NavContainerDesktop className={styles.navContainerDesktop}>
        <Layout.Back backCopy={t('common:back')} onBack={showPreviousPage} kind='tertiary' />
      </Layout.NavContainerDesktop>
      <ShopPNC
        {...coverageContent}
        {...coverageValues}
        HeaderWrapper={({ children }) => (
          <SectionBackground isHeader withLightningBorder>
            {children}
          </SectionBackground>
        )}
        eventHandlers={{
          onSubmit: showNextPage,
          onError: (error) => console.log('form is not complete', error),
          onBack: showPreviousPage,
          onSelectDeductible,
          onPropertyChange,
          onSelect,
          onToggle,
        }}
      >
        <Modal open={hasModalOpen} trackingId='rc-pro' onClose={() => setHasModalOpen(false)}>
          <LiabilityCardOverlay riskCarrier={riskCarrier} />
        </Modal>
        <Section>
          <SectionHeader>
            <SectionHeader.Heading>{t('funnel:coverage_liability_title')}</SectionHeader.Heading>
            <SectionHeader.Paragraph>{t('funnel:coverage_liability_example_text')}</SectionHeader.Paragraph>
            <SectionHeader.ModalButton onClick={() => setHasModalOpen(true)}>
              {t('funnel:coverage_show_detail_button')}
            </SectionHeader.ModalButton>
          </SectionHeader>
          <TrackedDiv objectiv={{ id: 'rc-pro' }}>
            <h3 className={headingXs}>{t('funnel:coverage_liability_options_label')}</h3>
            {conditionsData?.deductibleOptions?.length === 1 && (
              <p className={styles.includedDeductible}>
                <ShieldCheck className={styles.includedDeductibleIcon} />
                <Trans
                  className={textMd}
                  i18nKey='funnel:coverage_included_deductible'
                  values={{ amount: formatMoney(conditionsData.deductibleOptions[0], 'fr', { withoutFraction: true }) }}
                />
              </p>
            )}
            <CoverageInputSelect<CoverageKey>
              className={styles.coverageInputSelect}
              name='professionalLiability'
              options={professionalLiabilityOptions}
              required
              data-testid='professional-liability-options'
            />
            <ContactUs>{t('funnel:coverage_liability_contact_us_text_rc_pro')}</ContactUs>
          </TrackedDiv>
        </Section>
        <Section>
          <TrackedDiv objectiv={{ id: 'add-ons' }}>
            <AddOns addOns={addOns} heading={t('funnel:add_ons_shop_title')} />
          </TrackedDiv>
        </Section>
        {conditionsData?.deductibleOptions?.length !== 1 && (
          <Section>
            <DeductibleInput />
          </Section>
        )}
        {ghostCardsWithoutCarInsurance.length !== 0 ? (
          <Section>
            <SectionHeader>
              <SectionHeader.Heading>{t('funnel:coverage_ghost_card_section_heading')}</SectionHeader.Heading>
              <SectionHeader.Paragraph>{t('funnel:coverage_ghost_card_section_description')}</SectionHeader.Paragraph>
            </SectionHeader>
            {ghostCardsWithoutCarInsurance.map((slug) => {
              const GhostCard = slugToGhostCard[slug];

              return <GhostCard key={slug} className={styles.ghostCard} />;
            })}
          </Section>
        ) : null}
        {ghostCards.includes('carInsurance') && (
          <Section>
            <SectionHeader>
              <div className={clsx(headingXxxs, styles.label)}>{t('common:new')}</div>
              <SectionHeader.Heading>
                {t('funnel:coverage_ghost_card_section_car_insurance_heading')}
              </SectionHeader.Heading>
              <SectionHeader.Paragraph>
                {t('funnel:coverage_ghost_card_section_car_insurance_description')}
              </SectionHeader.Paragraph>
            </SectionHeader>
            <CarInsurance />
          </Section>
        )}
        <Section>
          <USP />
        </Section>
        <Section>
          <Receipt
            className={styles.receipt}
            premiums={coverageValues.premiums}
            fees={coverageValues.fees ?? []}
            hasPricingError={!!error && !(error instanceof RequestErrorClientSideError)}
            {...coverageContent}
            receiptBelowSubmit={<BelowSubmit riskCarrier={riskCarrier} />}
            addOns={addOns.filter((addOn) => addOn.id !== 'publicLiability')} // Public liability is handled together with RCPRo in the receipt
            hint={getRealEstateAgentHintIfApplicable()}
          />
        </Section>
        {!isDesktop && (
          <Section>
            <ContactOptions
              contactOptionsTitle={coverageContent.contactOptionsTitle}
              contactOptionsDescription={coverageContent.contactOptionsDescription}
              contactOptions={coverageContent.contactOptions}
            />
          </Section>
        )}
      </ShopPNC>
    </div>
  );
};
